import * as React from "react";
import { cx } from "../internal/cx.js";

export type TypographyVariant =
  | "caption"
  | "body"
  | "body-large"
  | "body-small"
  | "body-mono"
  | "body-mono-small"
  | "meta"
  | "heading-small"
  | "heading-medium"
  | "heading-large"
  | "heading-xl"
  | "overline"
  | "display";

const DEFAULT_ELEMENT_BY_VARIANT: Record<TypographyVariant, keyof React.JSX.IntrinsicElements> = {
  caption: "span",
  body: "p",
  "body-large": "p",
  "body-small": "p",
  "body-mono": "p",
  "body-mono-small": "p",
  meta: "span",
  "heading-small": "h3",
  "heading-medium": "h2",
  "heading-large": "h1",
  "heading-xl": "h2",
  overline: "span",
  display: "h1",
};

export interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Override the rendered element. If omitted, picks a semantic default
   * per variant (e.g. `display` → `<h1>`, `body` → `<p>`).
   * The chosen element does not affect typography styling — variants own
   * size / weight / leading, the element owns the document outline.
   */
  as?: keyof React.JSX.IntrinsicElements;
  /**
   * Dim the text using `--cw-v2-color-text-muted`. Use for secondary
   * captions, helper text, timestamps. Never for headings.
   */
  muted?: boolean;
  variant?: TypographyVariant;
}

/**
 * The single typography atom for the Cakewalk design system. Picks a
 * sensible HTML element by variant and applies the `cw-v2-typography--*`
 * class from `components.css`.
 *
 * Sizes come from the responsive `--cw-v2-font-size-*` tokens in
 * `typography-scale.css` (mobile-first: larger on phones, compact on
 * desktop). Prefer this component over per-breakpoint Tailwind text
 * utilities so copy stays aligned with the shared scale.
 */
export function Typography({
  as,
  className,
  muted = false,
  variant = "body",
  ...props
}: TypographyProps) {
  const Component = as ?? DEFAULT_ELEMENT_BY_VARIANT[variant];
  return React.createElement(Component, {
    className: cx(
      "cw-v2-typography",
      `cw-v2-typography--${variant}`,
      muted && "cw-v2-typography--muted",
      className,
    ),
    ...props,
  });
}
